import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import { alpha } from "@mui/material/styles";
import CalendarViewDay from "@mui/icons-material/CalendarViewDay";
import CalendarViewWeek from "@mui/icons-material/CalendarViewWeek";
import SuiBox from "components/SuiBox";
import CustomButton from "containers/CustomButton";

export const fullWidthButtonStyle = {
  marginBottom: 0,
  minHeight: 44,
  width: "100%",
  padding: "12px 16px",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
};

function iconCircleSx(size, bgAlpha, navy) {
  return {
    flexShrink: 0,
    width: size,
    height: size,
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    bgcolor: alpha(navy, bgAlpha),
    color: navy,
  };
}

/** Day-by-day path: outlined CTA and lighter card (secondary emphasis). */
export function DayByDayChoiceBlock({ navy, muted, theme, onSelect }) {
  const borderMedium = alpha(navy, 0.2);

  return (
    <Box
      sx={{
        p: { xs: 2, sm: 2.25 },
        borderRadius: 2,
        bgcolor: theme.palette.common.white,
        border: `1px solid ${borderMedium}`,
        transition: "border-color 0.2s ease, background-color 0.2s ease",
        "&:hover": {
          bgcolor: alpha(navy, 0.02),
          borderColor: alpha(navy, 0.28),
        },
      }}
    >
      <Stack direction="row" spacing={2} alignItems="flex-start">
        <Box aria-hidden sx={iconCircleSx(40, 0.06, navy)}>
          <CalendarViewDay sx={{ fontSize: 22 }} />
        </Box>
        <Stack spacing={1.25} flex={1} minWidth={0}>
          <Typography variant="subtitle2" sx={{ color: navy, fontWeight: 600, fontSize: "0.875rem" }}>
            Day by day
          </Typography>
          <Typography variant="body2" sx={{ color: muted, lineHeight: 1.55, fontSize: "0.8125rem" }}>
            One day at a time using the standard plan flow.
          </Typography>
          <SuiBox
            sx={{
              mt: 0.5,
              "& .MuiButton-root": {
                textTransform: "none",
                fontWeight: 600,
                fontSize: "0.8125rem",
                borderRadius: "10px",
                borderWidth: 1.5,
                transition: "background-color 0.15s ease, box-shadow 0.15s ease",
                "&:hover": {
                  bgcolor: alpha(navy, 0.04),
                  boxShadow: `0 2px 8px ${alpha(navy, 0.08)}`,
                },
                "&:active": { bgcolor: alpha(navy, 0.08) },
                "&:focus-visible": {
                  outline: `2px solid ${theme.palette.info.main}`,
                  outlineOffset: 3,
                },
              },
            }}
          >
            <CustomButton
              color="dark"
              variant="outlined"
              onClick={onSelect}
              style={fullWidthButtonStyle}
              title="Add day by day"
            />
          </SuiBox>
        </Stack>
      </Stack>
    </Box>
  );
}

/** Weekly grid path: primary emphasis (surface, shadow, gradient CTA). */
export function WeeklyGridChoiceBlock({ navy, muted, theme, onSelect }) {
  const softSurface = alpha(navy, 0.06);
  const borderSoft = alpha(navy, 0.12);

  return (
    <Box
      sx={{
        p: { xs: 2, sm: 2.5 },
        borderRadius: 2,
        bgcolor: softSurface,
        border: `1px solid ${borderSoft}`,
        boxShadow: `0 6px 20px ${alpha(navy, 0.08)}`,
        transition: "box-shadow 0.2s ease",
        "&:hover": { boxShadow: `0 8px 24px ${alpha(navy, 0.1)}` },
      }}
    >
      <Stack direction="row" spacing={2} alignItems="flex-start">
        <Box aria-hidden sx={iconCircleSx(44, 0.1, navy)}>
          <CalendarViewWeek sx={{ fontSize: 24 }} />
        </Box>
        <Stack spacing={1.25} flex={1} minWidth={0}>
          <Typography variant="subtitle2" sx={{ color: navy, fontWeight: 700, fontSize: "0.9375rem" }}>
            Full week in a grid
          </Typography>
          <Typography variant="body2" sx={{ color: muted, lineHeight: 1.55, fontSize: "0.8125rem" }}>
            All days in a grid for up to one week (per-day layout).
          </Typography>
          <SuiBox
            sx={{
              mt: 0.5,
              "& .MuiButton-root": {
                textTransform: "none",
                fontWeight: 600,
                fontSize: "0.875rem",
                borderRadius: "10px",
                boxShadow: `0 4px 12px ${alpha(navy, 0.25)}`,
                transition: "transform 0.15s ease, box-shadow 0.15s ease",
                "&:hover": { boxShadow: `0 6px 16px ${alpha(navy, 0.32)}` },
                "&:active": { transform: "translateY(1px)" },
                "&:focus-visible": {
                  outline: `2px solid ${theme.palette.info.main}`,
                  outlineOffset: 3,
                },
              },
            }}
          >
            <CustomButton
              color="dark"
              variant="gradient"
              onClick={onSelect}
              style={fullWidthButtonStyle}
              title="Build the full week"
            />
          </SuiBox>
        </Stack>
      </Stack>
    </Box>
  );
}

/** Visual separator between the two mutually exclusive choices. */
export function ChoiceOrDivider({ navy, muted }) {
  const borderSoft = alpha(navy, 0.12);
  return (
    <Stack direction="row" alignItems="center" spacing={1.5} sx={{ my: 2 }}>
      <Divider sx={{ flex: 1, borderColor: borderSoft }} />
      <Typography
        variant="caption"
        sx={{
          color: muted,
          fontWeight: 600,
          letterSpacing: "0.06em",
          textTransform: "uppercase",
          fontSize: "0.65rem",
        }}
      >
        or
      </Typography>
      <Divider sx={{ flex: 1, borderColor: borderSoft }} />
    </Stack>
  );
}
